import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";

import { Edit } from "@mui/icons-material";
import { Alert, Card, LinearProgress, Typography } from "@mui/material";

import _ from "lodash";
import { StyledButton } from "src/components/StyledComponents";
import { PLANNING_TOOL_SUPPLIER } from "src/constants/permissions";
import { TOrderVariantError } from "src/redux/slices/ordering/orderSetSlice";
import { formatMoneyString } from "src/utility/utilityFunctions";

import useOrderSetBudgetStatus from "@features/budgets/useOrderSetBudgetStatus";
import { getComplianceWarnings } from "@features/compliance/orderSetCompliance";
import { useMutateError } from "@features/errors";
import { useRealmUrl } from "@features/navigation/useRealmUrl";
import { useOptionalDistributorOrderingCtx } from "@features/orderingCtx/DistributorOrderingContext";
import { Button, FaIcon } from "@features/ui";
import { TriggeredRuleStatus } from "@models/TriggeredRule";
import { cn } from "@utils/tailwind";
import useRoleIs from "@utils/useRoleIs";

import CompareCosts from "../CompareCosts";
import { getCoopExtendedCost } from "../helpers";
import { useCurrentOrderSet } from "../orderSet/data/CurrentOrderSetContext";
import useDeleteOrderSetMutation from "../orderSet/data/mutations/useDeleteOrderSetMutation";
import useSubmitOrderSetMutation from "../orderSet/data/mutations/useSubmitOrderSetMutation";
import useUpdateOrderSetMutation from "../orderSet/data/mutations/useUpdateOrderSetMutation";
import SetIsWorkInProgressButton from "../preOrder/SetIsWorkInProgressButton";
import DistributorPurchaseOrderNumberInput from "./DistributorPurchaseOrderNumberInput";
import OrderSetComplianceWarnings from "./OrderSetComplianceWarnings";
import OrderSetWrapupButton from "./OrderSetWrapupButton";

const OrderSetSummaryActionPanel = () => {
  const navigate = useNavigate();
  const roleIs = useRoleIs();
  const realmUrl = useRealmUrl();
  const distCtx = useOptionalDistributorOrderingCtx();
  const setMutateError = useMutateError();
  const deleteOrderSet = useDeleteOrderSetMutation();
  const submitOrderSet = useSubmitOrderSetMutation();
  const updateOrderSet = useUpdateOrderSetMutation();
  const { orderSet, orders, isFetching, isCoopOrderSet } = useCurrentOrderSet();
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const {
    overBudgetBudgets,
    isOverBudget,
    isLoading: isLoadingBudgets,
    isCouponSpendUnavailable,
  } = useOrderSetBudgetStatus();
  const orderVariantErrors: Record<string, TOrderVariantError> = useSelector(
    (state: any) => state.orderSet.orderVariantErrors
  );
  // These survive the trip from the order table, which shares this route and
  // its CurrentOrderSetProvider, switching on the #overview hash.
  const hasOrderVariantErrors = Object.keys(orderVariantErrors).length > 0;

  const {
    totalEstimatedCost,
    totalEstimatedFreight,
    totalEstimatedTaxes,
    totalBeaconCost,
    totalQuantity,
    type: orderSetType,
    status,
  } = orderSet;

  // For inventory order sets, we can submit if there are no warnings or only prior or internal approval pending warnings
  const canSubmit = useMemo(() => {
    if (orderSetType !== "from-inventory") return true;

    const warnings = getComplianceWarnings(orders);

    return (
      warnings.filter(
        (w) =>
          (w.type === "prior-approval" ||
            w.type === "coupon-prior-approval" ||
            w.type === "internal-approval" ||
            w.type === "coupon-internal-approval") &&
          w.status === TriggeredRuleStatus.PRIOR_APPROVAL_PENDING
      ).length === warnings.length
    );
  }, [orderSetType, orders]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (submitOrderSet.isSuccess) {
      timeout = setTimeout(() => {
        const redirectTo = roleIs(PLANNING_TOOL_SUPPLIER)
          ? "/"
          : realmUrl("/orders/history/order");
        navigate(redirectTo, { replace: true });
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [submitOrderSet.isSuccess, navigate, roleIs, realmUrl]);

  const billableTotalEstimatedCost = _.sumBy(
    orders,
    (o) => +o.billableTotalEstimatedCost
  );

  const billableTotalEstimatedShippingCost = _.sumBy(
    orders,
    (o) => +o.billableTotalEstimatedShippingCost
  );

  const billableTotalEstimatedTax = _.sumBy(
    orders,
    (o) => +o.billableTotalEstimatedTax
  );

  const billableTotalBeaconCost = _.sumBy(
    orders,
    (o) => +o.billableTotalBeaconCost
  );

  const billableExtendedCost =
    billableTotalEstimatedCost +
    billableTotalEstimatedShippingCost +
    billableTotalEstimatedTax +
    billableTotalBeaconCost;

  const extendedCostWithCoopDiscount = isCoopOrderSet
    ? _.sumBy(orders, (o) => getCoopExtendedCost(o, o.orderingType))
    : billableExtendedCost;

  const coopDiscount = billableExtendedCost - extendedCostWithCoopDiscount;

  const handleSubmitOrderSet = () => {
    setIsUpdateLoading(true);
    submitOrderSet
      .mutateAsync()
      .catch(setMutateError)
      .finally(() => setIsUpdateLoading(false));
  };

  const toggleIsWorkComplete = () => {
    setIsUpdateLoading(true);
    return updateOrderSet
      .mutateAsync({
        isWorkComplete: !orderSet?.isWorkComplete,
      })
      .catch(setMutateError)
      .finally(() => setIsUpdateLoading(false));
  };

  const rows = [
    [
      "Total Item Cost",
      <CompareCosts
        cost={billableTotalEstimatedCost}
        comparedTo={totalEstimatedCost}
      />,
    ],
    [
      "Freight",
      <CompareCosts
        cost={billableTotalEstimatedShippingCost}
        comparedTo={totalEstimatedFreight}
      />,
    ],
    [
      "Taxes",
      <CompareCosts
        cost={billableTotalEstimatedTax}
        comparedTo={totalEstimatedTaxes}
      />,
    ],
    [
      "Beacon Cost",
      <CompareCosts
        cost={billableTotalBeaconCost}
        comparedTo={totalBeaconCost}
      />,
    ],
  ];
  return (
    <>
      <Card className="relative">
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <Button
              component={Link}
              to="#"
              variant="contained"
              color="secondary"
              startIcon={<Edit />}
              className="whitespace-nowrap"
            >
              Edit Order
            </Button>
            <OrderSetWrapupButton />
          </div>
          <div className="space-y-4">
            <div
              className={cn(
                "grid max-w-full grid-cols-[auto_1fr] gap-1 overflow-hidden transition-opacity text-neutral-500",
                isFetching && "opacity-50"
              )}
            >
              <div>Total Items</div>
              <div className="text-right">{totalQuantity}</div>

              {rows.map(([label, value], i) => (
                <React.Fragment key={i}>
                  <Typography noWrap className="text-neutral-500 text-base">
                    {label}
                  </Typography>
                  <div className="text-right text-neutral-700 font-medium">
                    {typeof value !== "object"
                      ? formatMoneyString(value)
                      : value}
                  </div>
                </React.Fragment>
              ))}
            </div>
            {isCoopOrderSet && coopDiscount > 0 && (
              <>
                <div className="flex gap-4 justify-between font-medium text-neutral-700">
                  <span>Sub Total</span>
                  <span className="text-right font-medium">
                    {formatMoneyString(billableExtendedCost)}
                  </span>
                </div>
                <div className="flex gap-4 justify-between px-2 py-1 !mt-2 -mx-2 bg-primary-50 text-primary-600 rounded">
                  <span>Co-Op Coverage (50%)</span>
                  <span className="text-right font-medium">
                    {formatMoneyString(-coopDiscount)}
                  </span>
                </div>
              </>
            )}
            <div
              className={cn(
                "flex items-baseline justify-between text-lg text-neutral-700 font-medium",
                isFetching && "opacity-50"
              )}
            >
              <span>{isCoopOrderSet ? "Your Cost" : "Est. Extended Cost"}</span>
              <span>{formatMoneyString(extendedCostWithCoopDiscount)}</span>
            </div>
          </div>
          {distCtx?.orderingCtx && <DistributorPurchaseOrderNumberInput />}
          {!!orderSet.market && (
            <Alert
              severity="warning"
              className="-m-2 mb-0 text-sm rounded-lg bg-amber-100 text-amber-900"
            >
              By obtaining items from this system, distributor representative
              affirms that distributor intends to use the item only in
              accordance with state and federal law.
            </Alert>
          )}

          {orderSetType === "pre-order" ? (
            <SetIsWorkInProgressButton
              toggleWorkInProgress={toggleIsWorkComplete}
              isUpdateLoading={isUpdateLoading}
            />
          ) : (
            <>
              {["inactive", "in-progress"].includes(status) && (
                <>
                  {isOverBudget ? (
                    <Alert
                      severity="error"
                      className="-m-2 mb-0 text-sm rounded-lg"
                    >
                      This order exceeds the available budget for{" "}
                      {overBudgetBudgets.map((b) => b.brandName).join(", ")}.
                      Reduce quantities before submitting.
                    </Alert>
                  ) : (
                    isCouponSpendUnavailable && (
                      <Alert
                        severity="error"
                        className="-m-2 mb-0 text-sm rounded-lg"
                      >
                        Coupon redemptions and fees could not be loaded, so the
                        remaining budget is unknown. Reload the page to try
                        again.
                      </Alert>
                    )
                  )}
                  {hasOrderVariantErrors && (
                    <Alert
                      severity="error"
                      className="-m-2 mb-0 text-sm rounded-lg"
                    >
                      Some items could not be updated. Resolve the errors on the
                      order before submitting.
                    </Alert>
                  )}
                  <StyledButton
                    fullWidth
                    cta
                    disabled={
                      isFetching ||
                      !canSubmit ||
                      isOverBudget ||
                      isLoadingBudgets ||
                      isCouponSpendUnavailable ||
                      hasOrderVariantErrors
                    }
                    loading={isUpdateLoading}
                    onClick={handleSubmitOrderSet}
                  >
                    Submit Order
                  </StyledButton>
                </>
              )}

              <OrderSetComplianceWarnings />

              {status === "submitted" && (
                <div className="text-lg text-neutral-700">
                  {orderSetType === "from-inventory"
                    ? "Thank you for your submission. Orders are transmitted sequentially to the warehouse in the order they are received and thus may take a few minutes to process. Redirecting you in 5 seconds..."
                    : "Thank you for your submission. Redirecting you in 5 seconds..."}
                </div>
              )}
            </>
          )}
        </div>
        {isFetching && <LinearProgress className="absolute inset-0 top-auto" />}
      </Card>
      {orderSetType !== "pre-order" && (
        <div className="mt-4 flex justify-end">
          <Button
            variant="danger"
            onClick={() =>
              window.confirm(
                "Are you sure you want to delete this order-set?"
              ) && deleteOrderSet.mutate()
            }
            startIcon={
              <FaIcon icon="trash" variant="solid" className="!text-sm" />
            }
          >
            Delete Order Set
          </Button>
        </div>
      )}
    </>
  );
};

export default OrderSetSummaryActionPanel;
